/** Cheapest current itineraries for a watch, segmented by itinerary shape. Prop-driven. */
import type { CheapestShapeGroup, FlightObservation } from '../../types/flights';
import type { ItineraryGroupKey } from './flightFilters';
import { hasStopover, tripShape } from '../../../../server/lib/flight-shape.ts';
import { carrierLabel } from './carrierLabel';
import type { RunFreshness } from './observationFreshness';
import {
  formatObservationAge,
  formatObservedAtAbsolute,
  isFromLastRun,
} from './observationFreshness';

export interface CheapestItinerariesTableProps {
  groups: CheapestShapeGroup[];
  emptyMessage?: string;
  freshness?: RunFreshness | null;
  /** Injectable for deterministic gallery/demo rendering. Defaults to Date.now(). */
  now?: number;
}

function shapeBadge(o: FlightObservation): string {
  const shape = tripShape(o);
  if (hasStopover(o)) {
    return `${shape} via ${o.stopover_point}${o.stopover_nights ? ` · ${o.stopover_nights}n` : ''}`;
  }
  return shape;
}

function groupHeader(key: ItineraryGroupKey): string {
  return key;
}

export function CheapestItinerariesTable({
  groups,
  emptyMessage,
  freshness,
  now: nowProp,
}: CheapestItinerariesTableProps) {
  // eslint-disable-next-line react-hooks/purity
  const now = nowProp ?? Date.now();
  const hasRows = groups.some((g) => g.rows.length > 0);
  if (!hasRows) {
    return (
      <p className="text-sm text-gray-500">
        {emptyMessage ?? 'No priced observations yet.'}
      </p>
    );
  }

  const showCaption = freshness != null && freshness.lastRunAt != null;

  return (
    <div className="overflow-x-auto">
      {showCaption && (
        <p className="text-xs text-gray-500 mb-2">
          Last run {formatObservedAtAbsolute(freshness.lastRunAt!)} ·{' '}
          {freshness.refreshed} of {freshness.total} rows refreshed
        </p>
      )}
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
            <th className="py-2 pr-4">Price</th>
            <th className="py-2 pr-4">Route</th>
            <th className="py-2 pr-4">Carrier</th>
            <th className="py-2 pr-4">Dates</th>
            <th className="py-2 pr-4">Shape</th>
            <th className="py-2 pr-4">Fare family</th>
            <th className="py-2 pr-4">Note</th>
            <th className="py-2">Age</th>
          </tr>
        </thead>
        {groups.map((g) => (
          <tbody key={g.group_key}>
            <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <td colSpan={8} className="py-1.5 px-2">
                {groupHeader(g.group_key)}
                {g.count > g.rows.length && (
                  <span className="ml-2 normal-case text-gray-400">
                    showing {g.rows.length} of {g.count}
                  </span>
                )}
              </td>
            </tr>
            {g.rows.map((r) => {
              const label = carrierLabel(r.carrier);
              const stale =
                freshness != null && !isFromLastRun(r.observed_at, freshness.cutoffAt);
              const rowClass = stale
                ? 'border-b border-gray-100 last:border-0 text-gray-400'
                : 'border-b border-gray-100 last:border-0';
              return (
                <tr key={r.id} className={rowClass}>
                  <td className={`py-2 pr-4 ${stale ? 'font-medium' : 'font-semibold'} whitespace-nowrap`}>
                    {r.currency} {Math.round(r.price ?? 0)}
                  </td>
                  <td className="py-2 pr-4 font-mono whitespace-nowrap">
                    {r.origin} → {r.destination}
                  </td>
                  <td className="py-2 pr-4 font-mono" title={label.title}>
                    {label.text}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {r.outbound_date}
                    {r.inbound_date ? ` → ${r.inbound_date}` : ' (oneway)'}
                  </td>
                  <td className="py-2 pr-4">
                    <span
                      className={
                        hasStopover(r)
                          ? 'inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs'
                          : 'inline-block px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs'
                      }
                    >
                      {shapeBadge(r)}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-xs text-gray-600">{r.fare_family || '—'}</td>
                  <td className="py-2 pr-4 text-xs text-gray-500">
                    {hasStopover(r) && !r.hotel_offer_note && (
                      <span className="text-amber-700">hotel offer: check manually</span>
                    )}
                    {r.hotel_offer_note}
                  </td>
                  <td className="py-2 whitespace-nowrap" title={formatObservedAtAbsolute(r.observed_at)}>
                    <span className="inline-flex items-center gap-1.5">
                      {formatObservationAge(r.observed_at, now)}
                      {stale && (
                        <span className="border border-gray-300 text-gray-500 text-[10px] rounded-full px-1.5">
                          not in last run
                        </span>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        ))}
      </table>
    </div>
  );
}
